import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type MetaFunction } from 'react-router'

export const handle: SEOHandle = {
	getSitemapEntries: () => [{ route: '/legal-notice/eula', priority: 0.7 }],
}

export const meta: MetaFunction = ({ location }) => {
	const title = `End User License Agreement | Legal Notice | ${import.meta.env.VITE_APP_TITLE}`
	const description = `End User License Agreement for ${import.meta.env.VITE_APP_TITLE} users`

	const url = `https://www.trailviewr.com${location.pathname}`

	return [
		{ title },
		{ name: 'description', content: description },
		{ tagName: 'link', rel: 'canonical', href: url },

		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:url', content: url },

		{ name: 'twitter:title', content: title },
		{ name: 'twitter:description', content: description },
		{ name: 'twitter:url', content: url },

		{
			'script:ld+json': {
				'@context': 'https://schema.org',
				'@type': 'WebPage',
				name: title,
				description,
				url,
			},
		},
	]
}

export default function EulaRoute() {
	return (
		<main className="mx-auto max-w-4xl px-4 py-8">
			<h1 className="mb-6 text-3xl font-bold">END USER LICENSE AGREEMENT</h1>
			<p className="mb-4">
				<strong className="mr-2">Last updated</strong>
				<strong>October 6, 2025</strong>
			</p>
			<p className="mb-4">
				TrailViewr.com is licensed to You (End-User) by Knight Rider Consulting,
				Inc. , located and registered at 5900 Balcones Drive # 19816, Austin,
				Texas 78731, United States ("<strong>Licensor</strong>"), for use only
				under the terms of this License Agreement.
			</p>
			<p className="mb-4">
				By downloading the Licensed Application from , and any update thereto
				(as permitted by this License Agreement), You indicate that You agree to
				be bound by all of the terms and conditions of this License Agreement,
				and that You accept this License Agreement. referred to in this License
				Agreement as "<strong>Services</strong>."
			</p>
			<p className="mb-4">
				The parties of this License Agreement acknowledge that the Services are
				not a Party to this License Agreement and are not bound by any
				provisions or obligations with regard to the Licensed Application, such
				as warranty, liability, maintenance and support thereof. Knight Rider
				Consulting, Inc. , not the Services, is solely responsible for the
				Licensed Application and the content thereof.
			</p>
			<p className="mb-4">
				This License Agreement may not provide for usage rules for the Licensed
				Application that are in conflict with the latest ("
				<strong>Usage Rules</strong>"). Knight Rider Consulting, Inc.
				acknowledges that it had the opportunity to review the Usage Rules and
				this License Agreement is not conflicting with them.
			</p>
			<p className="mb-4">
				TrailViewr.com when purchased or downloaded through the Services, is
				licensed to You for use only under the terms of this License Agreement.
				The Licensor reserves all rights not expressly granted to You.
				TrailViewr.com is to be used on devices that operate with .
			</p>
			<h2 className="mt-6 mb-4 text-2xl font-bold">TABLE OF CONTENTS</h2>
			<ul className="mb-6 space-y-2">
				<li>
					<a href="#the-application" className="text-blue-600 hover:underline">
						1. THE APPLICATION
					</a>
				</li>
				<li>
					<a href="#scope-of-license" className="text-blue-600 hover:underline">
						2. SCOPE OF LICENSE
					</a>
				</li>
				<li>
					<a
						href="#technical-requirements"
						className="text-blue-600 hover:underline"
					>
						3. TECHNICAL REQUIREMENTS
					</a>
				</li>
				<li>
					<a
						href="#maintenance-and-support"
						className="text-blue-600 hover:underline"
					>
						4. MAINTENANCE AND SUPPORT
					</a>
				</li>
				<li>
					<a href="#use-of-data" className="text-blue-600 hover:underline">
						5. USE OF DATA
					</a>
				</li>
				<li>
					<a
						href="#user-generated-contributions"
						className="text-blue-600 hover:underline"
					>
						6. USER-GENERATED CONTRIBUTIONS
					</a>
				</li>
				<li>
					<a
						href="#contribution-license"
						className="text-blue-600 hover:underline"
					>
						7. CONTRIBUTION LICENSE
					</a>
				</li>
				<li>
					<a href="#liability" className="text-blue-600 hover:underline">
						8. LIABILITY
					</a>
				</li>
				<li>
					<a href="#warranty" className="text-blue-600 hover:underline">
						9. WARRANTY
					</a>
				</li>
				<li>
					<a href="#product-claims" className="text-blue-600 hover:underline">
						10. PRODUCT CLAIMS
					</a>
				</li>
				<li>
					<a href="#legal-compliance" className="text-blue-600 hover:underline">
						11. LEGAL COMPLIANCE
					</a>
				</li>
				<li>
					<a
						href="#contact-information"
						className="text-blue-600 hover:underline"
					>
						12. CONTACT INFORMATION
					</a>
				</li>
				<li>
					<a href="#termination" className="text-blue-600 hover:underline">
						13. TERMINATION
					</a>
				</li>
				<li>
					<a
						href="#third-party-terms-of-agreements-and-beneficiary"
						className="text-blue-600 hover:underline"
					>
						14. THIRD-PARTY TERMS OF AGREEMENTS AND BENEFICIARY
					</a>
				</li>
				<li>
					<a
						href="#intellectual-property-rights"
						className="text-blue-600 hover:underline"
					>
						15. INTELLECTUAL PROPERTY RIGHTS
					</a>
				</li>
				<li>
					<a href="#applicable-law" className="text-blue-600 hover:underline">
						16. APPLICABLE LAW
					</a>
				</li>
				<li>
					<a href="#miscellaneous" className="text-blue-600 hover:underline">
						17. MISCELLANEOUS
					</a>
				</li>
			</ul>
			<h2 id="the-application" className="mt-6 mb-4 text-2xl font-bold">
				1. THE APPLICATION
			</h2>
			<p className="mb-4">
				TrailViewr.com ("<strong>Licensed Application</strong>") is a piece of
				software created to __________ — and customized for mobile devices ("
				<strong>Devices</strong>"). It is used to __________.
			</p>
			<p className="mb-4">
				The Licensed Application is not tailored to comply with
				industry-specific regulations (Health Insurance Portability and
				Accountability Act (HIPAA), Federal Information Security Management Act
				(FISMA), etc.), so if your interactions would be subjected to such laws,
				you may not use this Licensed Application. You may not use the Licensed
				Application in a way that would violate the Gramm-Leach-Bliley Act
				(GLBA).
			</p>
			<h2 id="scope-of-license" className="mt-6 mb-4 text-2xl font-bold">
				2. SCOPE OF LICENSE
			</h2>
			<p className="mb-4">
				2.1 You are given a non-transferable, non-exclusive, non-sublicensable
				license to install and use the Licensed Application on any Devices that
				You (End-User) own or control and as permitted by the Usage Rules, with
				the exception that such Licensed Application may be accessed and used by
				other accounts associated with You (End-User, The Purchaser) via Family
				Sharing or volume purchasing.
			</p>
			<p className="mb-4">
				2.2 This license will also govern any updates of the Licensed
				Application provided by Licensor that replace, repair, and/or supplement
				the first Licensed Application, unless a separate license is provided
				for such update, in which case the terms of that new license will
				govern.
			</p>
			<p className="mb-4">
				2.3 You may not share or make the Licensed Application available to
				third parties (unless to the degree allowed by the Usage Rules, and with
				Knight Rider Consulting, Inc. 's prior written consent), sell, rent,
				lend, lease or otherwise redistribute the Licensed Application.
			</p>
			<p className="mb-4">
				2.4 You may not reverse engineer, translate, disassemble, integrate,
				decompile, remove, modify, combine, create derivative works or updates
				of, adapt, or attempt to derive the source code of the Licensed
				Application, or any part thereof (except with Knight Rider Consulting,
				Inc. 's prior written consent).
			</p>
			<p className="mb-4">
				2.5 You may not copy (excluding when expressly authorized by this
				license and the Usage Rules) or alter the Licensed Application or
				portions thereof. You may create and store copies only on devices that
				You own or control for backup keeping under the terms of this license,
				the Usage Rules, and any other terms and conditions that apply to the
				device or software used. You may not remove any intellectual property
				notices. You acknowledge that no unauthorized third parties may gain
				access to these copies at any time. If you sell your Devices to a third
				party, you must remove the Licensed Application from the Devices before
				doing so.
			</p>
			<p className="mb-4">
				2.6 Violations of the obligations mentioned above, as well as the
				attempt of such infringement, may be subject to prosecution and damages.
			</p>
			<p className="mb-4">
				2.7 Licensor reserves the right to modify the terms and conditions of
				licensing.
			</p>
			<p className="mb-4">
				2.8 Nothing in this license should be interpreted to restrict
				third-party terms. When using the Licensed Application, You must ensure
				that You comply with applicable third-party terms and conditions.
			</p>
			<h2 id="technical-requirements" className="mt-6 mb-4 text-2xl font-bold">
				3. TECHNICAL REQUIREMENTS
			</h2>
			<p className="mb-4">
				3.1 You acknowledge that it is Your responsibility to confirm and
				determine that the app end-user device on which You intend to use the
				Licensed Application satisfies the technical specifications mentioned
				above.
			</p>
			<p className="mb-4">
				3.2 Licensor reserves the right to modify the technical specifications
				as it sees appropriate at any time.
			</p>
			<h2 id="maintenance-and-support" className="mt-6 mb-4 text-2xl font-bold">
				4. MAINTENANCE AND SUPPORT
			</h2>
			<p className="mb-4">
				4.1 The Licensor is solely responsible for providing any maintenance and
				support services for this Licensed Application. You can reach the
				Licensor at the email address listed in the Overview for this Licensed
				Application.
			</p>
			<p className="mb-4">
				4.2 Knight Rider Consulting, Inc. and the End-User acknowledge that the
				Services have no obligation whatsoever to furnish any maintenance and
				support services with respect to the Licensed Application.
			</p>
			<h2 id="use-of-data" className="mt-6 mb-4 text-2xl font-bold">
				5. USE OF DATA
			</h2>
			<p className="mb-4">
				You acknowledge that Licensor will be able to access and adjust Your
				downloaded Licensed Application content and Your personal information,
				and that Licensor's use of such material and information is subject to
				Your legal agreements with Licensor and Licensor's privacy policy:
				<a href="/privacy-policy" className="text-blue-600 hover:underline">
					/privacy-policy
				</a>
				.
			</p>
			<p className="mb-4">
				You acknowledge that the Licensor may periodically collect and use
				technical data and related information about your device, system, and
				application software, and peripherals, offer product support, facilitate
				the software updates, and for purposes of providing other services to
				you (if any) related to the Licensed Application. Licensor may also use
				this information to improve its products or to provide services or
				technologies to you, as long as it is in a form that does not personally
				identify you.
			</p>
			<h2
				id="user-generated-contributions"
				className="mt-6 mb-4 text-2xl font-bold"
			>
				6. USER-GENERATED CONTRIBUTIONS
			</h2>
			<p className="mb-4">
				The Licensed Application may invite you to chat, contribute to, or
				participate in blogs, message boards, online forums, and other
				functionality, and may provide you with the opportunity to create,
				submit, post, display, transmit, perform, publish, distribute, or
				broadcast content and materials to us or in the Licensed Application,
				including but not limited to text, writings, video, audio, photographs,
				graphics, comments, suggestions, or personal information or other
				material (collectively, "Contributions"). Contributions may be viewable
				by other users of the Licensed Application and through third-party
				websites or applications. As such, any Contributions you transmit may be
				treated as non-confidential and non-proprietary. When you create or make
				available any Contributions, you thereby represent and warrant that:
			</p>
			<ol className="mb-4 list-inside list-decimal space-y-2">
				<li className="ml-4">
					The creation, distribution, transmission, public display, or
					performance, and the accessing, downloading, or copying of your
					Contributions do not and will not infringe the proprietary rights,
					including but not limited to the copyright, patent, trademark, trade
					secret, or moral rights of any third party.
				</li>
				<li className="ml-4">
					You are the creator and owner of or have the necessary licenses,
					rights, consents, releases, and permissions to use and to authorize
					us, the Licensed Application, and other users of the Licensed
					Application to use your Contributions in any manner contemplated by
					the Licensed Application and this License Agreement.
				</li>
				<li className="ml-4">
					You have the written consent, release, and/or permission of each and
					every identifiable individual person in your Contributions to use the
					name or likeness or each and every such identifiable individual person
					to enable inclusion and use of your Contributions in any manner
					contemplated by the Licensed Application and this License Agreement.
				</li>
				<li className="ml-4">
					Your Contributions are not false, inaccurate, or misleading.
				</li>
				<li className="ml-4">
					Your Contributions are not unsolicited or unauthorized advertising,
					promotional materials, pyramid schemes, chain letters, spam, mass
					mailings, or other forms of solicitation.
				</li>
				<li className="ml-4">
					Your Contributions are not obscene, lewd, lascivious, filthy, violent,
					harassing, libelous, slanderous, or otherwise objectionable (as
					determined by us).
				</li>
				<li className="ml-4">
					Your Contributions do not ridicule, mock, disparage, intimidate, or
					abuse anyone.
				</li>
				<li className="ml-4">
					Your Contributions are not used to harass or threaten (in the legal
					sense of those terms) any other person and to promote violence against
					a specific person or class of people.
				</li>
				<li className="ml-4">
					Your Contributions do not violate any applicable law, regulation, or
					rule.
				</li>
				<li className="ml-4">
					Your Contributions do not violate the privacy or publicity rights of
					any third party.
				</li>
				<li className="ml-4">
					Your Contributions do not violate any applicable law concerning child
					pornography, or otherwise intended to protect the health or well-being
					of minors.
				</li>
				<li className="ml-4">
					Your Contributions do not include any offensive comments that are
					connected to race, national origin, gender, sexual preference, or
					physical handicap.
				</li>
				<li className="ml-4">
					Your Contributions do not otherwise violate, or link to material that
					violates, any provision of this License Agreement, or any applicable
					law or regulation.
				</li>
			</ol>
			<p className="mb-4">
				Any use of the Licensed Application in violation of the foregoing
				violates this License Agreement and may result in, among other things,
				termination or suspension of your rights to use the Licensed
				Application.
			</p>
			<h2 id="contribution-license" className="mt-6 mb-4 text-2xl font-bold">
				7. CONTRIBUTION LICENSE
			</h2>
			<p className="mb-4">
				By posting your Contributions to any part of the Licensed Application or
				making Contributions accessible to the Licensed Application by linking
				your account from the Licensed Application to any of your social
				networking accounts, you automatically grant, and you represent and
				warrant that you have the right to grant, to us an unrestricted,
				unlimited, irrevocable, perpetual, non-exclusive, transferable,
				royalty-free, fully-paid, worldwide right, and license to host, use
				copy, reproduce, disclose, sell, resell, publish, broadcast, retitle,
				archive, store, cache, publicly display, reformat, translate, transmit,
				excerpt (in whole or in part), and distribute such Contributions
				(including, without limitation, your image and voice) for any purpose,
				commercial advertising, or otherwise, and to prepare derivative works
				of, or incorporate in other works, such as Contributions, and grant and
				authorize sublicenses of the foregoing. The use and distribution may
				occur in any media formats and through any media channels.
			</p>
			<p className="mb-4">
				This license will apply to any form, media, or technology now known or
				hereafter developed, and includes our use of your name, company name,
				and franchise name, as applicable, and any of the trademarks, service
				marks, trade names, logos, and personal and commercial images you
				provide. You waive all moral rights in your Contributions, and you
				warrant that moral rights have not otherwise been asserted in your
				Contributions.
			</p>
			<p className="mb-4">
				We do not assert any ownership over your Contributions. You retain full
				ownership of all of your Contributions and any intellectual property
				rights or other proprietary rights associated with your Contributions.
			</p>
			<p className="mb-4">
				We have the right, in our sole and absolute discretion, (1) to edit,
				redact, or otherwise change any Contributions; (2) to recategorize any
				Contributions to place them in more appropriate locations in the
				Licensed Application; and (3) to prescreen or delete any Contributions
				at any time and for any reason, without notice. We have no obligation to
				monitor your Contributions.
			</p>
			<h2 id="liability" className="mt-6 mb-4 text-2xl font-bold">
				8. LIABILITY
			</h2>
			<p className="mb-4">
				8.1 Licensor takes no accountability or responsibility for any damages
				caused due to a breach of duties according to Section 2 of this License
				Agreement. To avoid data loss, You are required to make use of backup
				functions of the Licensed Application to the extent allowed by
				applicable third-party terms and conditions of use. You are aware that
				in case of alterations or manipulations of the Licensed Application, You
				will not have access to the Licensed Application.
			</p>
			<h2 id="warranty" className="mt-6 mb-4 text-2xl font-bold">
				9. WARRANTY
			</h2>
			<p className="mb-4">
				9.1 Licensor warrants that the Licensed Application is free of spyware,
				trojan horses, viruses, or any other malware at the time of Your
				download. Licensor warrants that the Licensed Application works as
				described in the user documentation.
			</p>
			<p className="mb-4">
				9.2 No warranty is provided for the Licensed Application that is not
				executable on the device, that has been unauthorizedly modified, handled
				inappropriately or culpably, combined or installed with inappropriate
				hardware or software, used with inappropriate accessories, regardless if
				by Yourself or by third parties, or if there are any other reasons
				outside of Knight Rider Consulting, Inc. 's sphere of influence that
				affect the executability of the Licensed Application.
			</p>
			<p className="mb-4">
				9.3 You are required to inspect the Licensed Application immediately
				after installing it and notify Knight Rider Consulting, Inc. about
				issues discovered without delay by email provided in
				<a
					href="#contact-information"
					className="ml-1 text-blue-600 hover:underline"
				>
					Contact Information
				</a>
				. The defect report will be taken into consideration and further
				investigated if it has been emailed within a period of seven (7) days
				after discovery.
			</p>
			<p className="mb-4">
				9.4 If we confirm that the Licensed Application is defective, Knight
				Rider Consulting, Inc. reserves a choice to remedy the situation either
				by means of solving the defect or substitute delivery.
			</p>
			<p className="mb-4">
				9.5 In the event of any failure of the Licensed Application to conform
				to any applicable warranty, You may notify the Services Store Operator,
				and Your Licensed Application purchase price will be refunded to You. To
				the maximum extent permitted by applicable law, the Services Store
				Operator will have no other warranty obligation whatsoever with respect
				to the Licensed Application, and any other losses, claims, damages,
				liabilities, expenses, and costs attributable to any negligence to
				adhere to any warranty.
			</p>
			<p className="mb-4">
				9.6 If the user is an entrepreneur, any claim based on faults expires
				after a statutory period of limitation amounting to twelve (12) months
				after the Licensed Application was made available to the user. The
				statutory periods of limitation given by law apply for users who are
				consumers.
			</p>

			<h2 id="product-claims" className="mt-6 mb-4 text-2xl font-bold">
				10. PRODUCT CLAIMS
			</h2>
			<p className="mb-4">
				Knight Rider Consulting, Inc. and the End-User acknowledge that Knight
				Rider Consulting, Inc., and not the Services, is responsible for
				addressing any claims of the End-User or any third party relating to the
				Licensed Application or the End-User's possession and/or use of that
				Licensed Application, including, but not limited to:
			</p>
			<ul className="mb-4 list-disc pl-6">
				<li className="mb-2">product liability claims;</li>
				<li className="mb-2">
					any claim that the Licensed Application fails to conform to any
					applicable legal or regulatory requirement; and
				</li>
				<li className="mb-2">
					claims arising under consumer protection, privacy, or similar
					legislation.
				</li>
			</ul>
			<h2 id="legal-compliance" className="mt-6 mb-4 text-2xl font-bold">
				11. LEGAL COMPLIANCE
			</h2>
			<p className="mb-4">
				You represent and warrant that You are not located in a country that is
				subject to a US Government embargo, or that has been designated by the
				US Government as a "terrorist supporting" country; and that You are not
				listed on any US Government list of prohibited or restricted parties.
			</p>
			<h2 id="contact-information" className="mt-6 mb-4 text-2xl font-bold">
				12. CONTACT INFORMATION
			</h2>
			<p className="mb-4">
				For general inquiries, complaints, questions or claims concerning the
				Licensed Application, please contact:
			</p>
			<address className="mb-4 not-italic">
				<p>Dale Lotts</p>
				<p>5900 Balcones Drive # 19816</p>
				<p>Austin, TX 78731</p>
				<p>United States</p>
				<p>
					<a
						href="mailto:hello@trailviewr.com"
						className="text-blue-600 hover:underline"
					>
						hello@trailviewr.com
					</a>
				</p>
			</address>
			<h2 id="termination" className="mt-6 mb-4 text-2xl font-bold">
				13. TERMINATION
			</h2>
			<p className="mb-4">
				The license is valid until terminated by Knight Rider Consulting, Inc.
				or by You. Your rights under this license will terminate automatically
				and without notice from Knight Rider Consulting, Inc. if You fail to
				adhere to any term(s) of this license. Upon License termination, You
				shall stop all use of the Licensed Application, and destroy all copies,
				full or partial, of the Licensed Application.
			</p>
			<h2
				id="third-party-terms-of-agreements-and-beneficiary"
				className="mt-6 mb-4 text-2xl font-bold"
			>
				14. THIRD-PARTY TERMS OF AGREEMENTS AND BENEFICIARY
			</h2>
			<p className="mb-4">
				Knight Rider Consulting, Inc. represents and warrants that Knight Rider
				Consulting, Inc. will comply with applicable third-party terms of
				agreement when using Licensed Application.
			</p>
			<p className="mb-4">
				In Accordance with Section 9 of the "Instructions for Minimum Terms of
				Developer's End-User License Agreement," subsidiaries shall be
				third-party beneficiaries of this End User License Agreement and — upon
				Your acceptance of the terms and conditions of this License Agreement,
				will have the right (and will be deemed to have accepted the right) to
				enforce this End User License Agreement against You as a third-party
				beneficiary thereof.
			</p>
			<h2
				id="intellectual-property-rights"
				className="mt-6 mb-4 text-2xl font-bold"
			>
				15. INTELLECTUAL PROPERTY RIGHTS
			</h2>
			<p className="mb-4">
				Knight Rider Consulting, Inc. and the End-User acknowledge that, in the
				event of any third-party claim that the Licensed Application or the
				End-User's possession and use of that Licensed Application infringes on
				the third party's intellectual property rights, Knight Rider Consulting,
				Inc., and not the Services, will be solely responsible for the
				investigation, defense, settlement, and discharge or any such
				intellectual property infringement claims.
			</p>
			<h2 id="applicable-law" className="mt-6 mb-4 text-2xl font-bold">
				16. APPLICABLE LAW
			</h2>
			<p className="mb-4">
				This License Agreement is governed by the laws of the State of Minnesota
				excluding its conflicts of law rules.
			</p>
			<h2 id="miscellaneous" className="mt-6 mb-4 text-2xl font-bold">
				17. MISCELLANEOUS
			</h2>
			<div className="mb-4 space-y-2">
				<p>
					17.1 If any of the terms of this agreement should be or become
					invalid, the validity of the remaining provisions shall not be
					affected. Invalid terms will be replaced by valid ones formulated in a
					way that will achieve the primary purpose.
				</p>
				<p>
					17.2 Collateral agreements, changes and amendments are only valid if
					laid down in writing. The preceding clause can only be waived in
					writing.
				</p>
			</div>
		</main>
	)
}
